import React, { Component } from 'react';

export function licenseNavigatorV2DefaultQuestions(locale = 'de') {
  if (locale === 'en') {
    return [
      {
        key: 'work_mode',
        title: 'How are you working on the project?',
        hint: 'Choose "alone" if you are handling this as an individual or freelancer. Choose team/company if several people or a business are behind the project.',
        options: [
          { key: 'solo', label: 'I work alone', summary: 'Individual, creator, or freelancer' },
          { key: 'team', label: 'We work as a team or company', summary: 'Company, agency, or internal team' }
        ]
      },
      {
        key: 'usage_context',
        title: 'What do you want to do with the music?',
        hint: 'Think about the main type of use: regular content like social, website, podcast, or webinar, or a classic broadcast, trailer, or VOD use.',
        options: [
          { key: 'digital_content', label: 'Regular content project', summary: 'For example social, website, podcast, webinar, recruiting, or regular video' },
          { key: 'broadcast', label: 'Broadcast / Trailer / VOD', summary: 'For example radio, TV, cinema, VOD, or trailers' }
        ]
      },
      {
        key: 'client_context',
        title: 'Is this for yourself or for a client?',
        hint: 'Choose client if the music will be used in a commissioned project or on channels that belong to your client.',
        options: [
          { key: 'own', label: 'For me / my company', summary: 'Own channels or own projects' },
          { key: 'client', label: 'For a client', summary: 'Client project or use on client channels' }
        ]
      },
      {
        key: 'distribution_scope',
        title: 'Is this one main project or a broader campaign?',
        hint: 'Choose one main project for a single clearly scoped production. Choose broader campaign if several assets, versions, or placements belong together.',
        options: [
          { key: 'contained', label: 'One main project', summary: 'A clearly scoped project with typical versions or cutdowns' },
          { key: 'multi_asset', label: 'A larger campaign with multiple assets', summary: 'Multiple assets, placements, or campaign deliverables in one shared framework' }
        ]
      }
    ];
  }

  return [
    {
      key: 'work_mode',
      title: 'Wie arbeiten Sie an dem Projekt?',
      hint: 'Wählen Sie allein, wenn Sie das Projekt als Einzelperson oder Freelancer umsetzen. Wählen Sie Team oder Unternehmen, wenn mehrere Personen oder eine Firma dahinterstehen.',
      options: [
        { key: 'solo', label: 'Ich arbeite allein', summary: 'Einzelperson, Creator oder Freelancer' },
        { key: 'team', label: 'Wir arbeiten als Team oder Unternehmen', summary: 'Firma, Agentur oder internes Team' }
      ]
    },
    {
      key: 'usage_context',
      title: 'Was möchten Sie mit der Musik umsetzen?',
      hint: 'Gemeint ist die geplante Hauptnutzung: normales Content-Projekt wie Social, Website, Podcast oder Webinar oder ein klassischer Broadcast-, Trailer- oder VOD-Einsatz.',
      options: [
        { key: 'digital_content', label: 'Normales Content-Projekt', summary: 'Zum Beispiel Social, Website, Podcast, Webinar, Recruiting oder normales Video' },
        { key: 'broadcast', label: 'Broadcast / Trailer / VOD', summary: 'Zum Beispiel Radio, TV, Kino, VOD oder Trailer' }
      ]
    },
    {
      key: 'client_context',
      title: 'Ist das Projekt für Sie selbst oder für einen Kunden?',
      hint: 'Wählen Sie Kunde, wenn die Musik in einem Auftragsprojekt oder auf Kanälen des Kunden genutzt wird.',
      options: [
        { key: 'own', label: 'Für mich / mein Unternehmen', summary: 'Eigene Kanäle oder eigene Projekte' },
        { key: 'client', label: 'Für einen Kunden', summary: 'Kundenprojekt oder Nutzung auf Kanälen des Kunden' }
      ]
    },
    {
      key: 'distribution_scope',
      title: 'Geht es um ein einzelnes Hauptprojekt oder um mehrere Assets?',
      hint: 'Ein Hauptprojekt ist ein einzelner klar abgegrenzter Spot, Trailer oder Film. Mehrere Assets meint eine Kampagne mit mehreren Motiven, Versionen oder Placements.',
      options: [
        { key: 'contained', label: 'Ein einzelnes Hauptprojekt', summary: 'Ein klar abgegrenztes Projekt mit typischen Versionen oder Cutdowns' },
        { key: 'multi_asset', label: 'Eine größere Kampagne mit mehreren Assets', summary: 'Mehrere Assets, Produktionen, Placements oder Kampagnen-Bausteine in einem gemeinsamen Rahmen' }
      ]
    }
  ];
}

class LicenseNavigatorV2 extends Component {
  constructor(props) {
    super(props);

    this.state = {
      answers: {},
      currentStepIndex: 0
    };
  }

  componentDidMount() {
    this.emitStateChange();
  }

  componentDidUpdate(_prevProps, prevState) {
    if (
      prevState.currentStepIndex !== this.state.currentStepIndex ||
      JSON.stringify(prevState.answers) !== JSON.stringify(this.state.answers)
    ) {
      this.emitStateChange();
    }
  }

  recommendationForAnswers(answers = this.state.answers) {
    if (answers.usage_context === 'broadcast') {
      if (answers.distribution_scope === 'multi_asset') {
        return {
          key: 'campaign_license',
          title: 'Campaign',
          reason: this.i18nValue('campaign_reason'),
          nextStep: this.i18nValue('campaign_next_step')
        };
      }

      return {
        key: 'premium_license',
        title: 'Premium',
        reason: this.i18nValue('premium_reason'),
        nextStep: this.i18nValue('premium_next_step')
      };
    }

    if (answers.work_mode === 'solo') {
      return {
        key: 'solo_license',
        title: 'Solo',
        reason: this.i18nValue('solo_reason'),
        nextStep: this.i18nValue('solo_next_step')
      };
    }

    return {
      key: 'business_license',
      title: 'Business',
      reason: this.i18nValue('business_reason'),
      nextStep: this.i18nValue('business_next_step')
    };
  }

  questions() {
    return this.props.questions && this.props.questions.length > 0 ? this.props.questions : licenseNavigatorV2DefaultQuestions(this.props.locale);
  }

  showIntro() {
    return this.props.showIntro !== false;
  }

  showSidebar() {
    return this.props.showSidebar !== false;
  }

  i18nValue(key) {
    if (this.props.i18n && this.props.i18n[key] !== undefined) {
      return this.props.i18n[key];
    }

    return LicenseNavigatorV2.defaultProps.i18n[key];
  }

  handleAnswer(questionKey, optionKey) {
    const nextAnswers = {
      ...this.state.answers,
      [questionKey]: optionKey
    };
    const nextQuestionCount = this.activeQuestions(nextAnswers).length;
    const nextIndex = Math.min(this.state.currentStepIndex + 1, nextQuestionCount);
    const shouldAutoAdvance = this.props.autoAdvanceOnComplete === true &&
      typeof this.props.onRecommendationConfigure === 'function' &&
      nextIndex >= nextQuestionCount;

    this.setState(
      {
        answers: nextAnswers,
        currentStepIndex: nextIndex
      },
      () => {
        if (!shouldAutoAdvance) {
          return;
        }

        this.props.onRecommendationConfigure(this.recommendationForAnswers(nextAnswers));
      }
    );
  }

  goBack() {
    const activeQuestions = this.activeQuestions();
    if (this.state.currentStepIndex <= 0 && activeQuestions.length === 0) {
      return;
    }

    const previousStepIndex = this.isComplete() ? activeQuestions.length - 1 : this.state.currentStepIndex - 1;
    if (previousStepIndex < 0) {
      return;
    }

    const previousQuestionKey = activeQuestions[previousStepIndex].key;
    const nextAnswers = { ...this.state.answers };
    delete nextAnswers[previousQuestionKey];

    this.setState({
      answers: nextAnswers,
      currentStepIndex: previousStepIndex
    });
  }

  restart() {
    this.setState({
      answers: {},
      currentStepIndex: 0
    });
  }

  activeQuestions(answers = this.state.answers) {
    const usageContext = answers.usage_context;
    const questions = this.questions();
    const baseQuestions = questions.filter((question) => ['work_mode', 'usage_context'].includes(question.key));

    if (!usageContext) {
      return baseQuestions;
    }

    if (usageContext === 'digital_content') {
      return questions.filter((question) => ['work_mode', 'usage_context', 'client_context'].includes(question.key));
    }

    if (usageContext === 'broadcast') {
      return questions.filter((question) => ['work_mode', 'usage_context', 'distribution_scope'].includes(question.key));
    }

    return questions;
  }

  maxQuestionCount(answers = {}) {
    const activeQuestions = this.activeQuestions(answers);
    const nextQuestion = activeQuestions.find((question) => !answers[question.key]);

    if (!nextQuestion) {
      return Object.keys(answers).length;
    }

    return Math.max(
      ...nextQuestion.options.map((option) => this.maxQuestionCount({
        ...answers,
        [nextQuestion.key]: option.key
      }))
    );
  }

  currentQuestion() {
    return this.activeQuestions()[this.state.currentStepIndex] || null;
  }

  isComplete() {
    return this.state.currentStepIndex >= this.activeQuestions().length;
  }

  selectedOption(question) {
    const selectedKey = this.state.answers[question.key];
    if (!selectedKey) {
      return null;
    }

    return question.options.find((option) => option.key === selectedKey) || null;
  }

  recommendation() {
    return this.recommendationForAnswers();
  }

  recommendationTone(key) {
    switch (key) {
      case 'solo_license':
        return 'license-navigator-v2-demo__result-card license-navigator-v2-demo__result-card--solo';
      case 'business_license':
        return 'license-navigator-v2-demo__result-card license-navigator-v2-demo__result-card--business';
      case 'premium_license':
        return 'license-navigator-v2-demo__result-card license-navigator-v2-demo__result-card--premium';
      case 'campaign_license':
        return 'license-navigator-v2-demo__result-card license-navigator-v2-demo__result-card--campaign';
      default:
        return 'license-navigator-v2-demo__result-card';
    }
  }

  progressPercent() {
    const questionCount = this.activeQuestions().length || 1;
    return Math.round((this.state.currentStepIndex / questionCount) * 100);
  }

  emitStateChange() {
    if (typeof this.props.onStateChange !== 'function') {
      return;
    }

    const activeQuestions = this.activeQuestions();
    const questionCount = activeQuestions.length;
    const isComplete = this.isComplete();
    const displayStep = questionCount === 0 ? 1 : Math.min(this.state.currentStepIndex + 1, questionCount);

    this.props.onStateChange({
      questionCount: questionCount,
      maxQuestionCount: this.maxQuestionCount(),
      currentStepIndex: this.state.currentStepIndex,
      displayStep: displayStep,
      isComplete: isComplete
    });
  }

  recommendationActionLabel(recommendation) {
    if (this.props.recommendationActionLabel) {
      return this.props.recommendationActionLabel;
    }

    if (this.props.locale === 'de') {
      return `${recommendation.title} auswählen`;
    }

    return `Choose ${recommendation.title}`;
  }

  configureActionLabel(recommendation) {
    if (this.props.configureActionLabel) {
      return this.props.configureActionLabel;
    }

    if (this.props.locale === 'de') {
      return 'Lizenz für mein Projekt anpassen';
    }

    return 'Customize license for my project';
  }

  renderSummary() {
    const answeredQuestions = this.activeQuestions().filter((question) => this.state.answers[question.key]);

    return (
      <ul className='license-navigator-v2-demo__summary-list'>
        {answeredQuestions.map((question) => {
          const option = this.selectedOption(question);

          return (
            <li className='license-navigator-v2-demo__summary-item' key={question.key}>
              <span className='license-navigator-v2-demo__summary-label'>{question.title}</span>
              <span className='license-navigator-v2-demo__summary-value'>{option ? option.label : ''}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  renderSidebarContext() {
    const sidebarContextItems = Array.isArray(this.props.sidebarContextItems) ? this.props.sidebarContextItems : [];

    if (sidebarContextItems.length === 0) {
      return null;
    }

    return (
      <div className='license-navigator-v2-demo__sidebar-section'>
        <ul className='license-navigator-v2-demo__summary-list'>
          {sidebarContextItems.map((item) => (
            <li className='license-navigator-v2-demo__summary-item' key={item.label}>
              <span className='license-navigator-v2-demo__summary-label'>{item.label}</span>
              <span className='license-navigator-v2-demo__summary-value'>{item.value}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  renderCurrentQuestion() {
    const question = this.currentQuestion();
    if (!question) {
      return null;
    }

    return (
      <div className='license-navigator-v2-demo__question-card'>
        <div className='license-navigator-v2-demo__question-meta'>
          <span className='license-navigator-v2-demo__question-step'>{this.state.currentStepIndex + 1}</span>
          <div>
            <div className='license-navigator-v2-demo__question-title'>{question.title}</div>
            <div className='license-navigator-v2-demo__question-hint'>{question.hint}</div>
          </div>
        </div>
        <div className='license-navigator-v2-demo__option-grid'>
          {question.options.map((option) => (
            <button
              type='button'
              className='license-navigator-v2-demo__option'
              key={option.key}
              onClick={() => this.handleAnswer(question.key, option.key)}
            >
              <span className='license-navigator-v2-demo__option-label'>{option.label}</span>
              <span className='license-navigator-v2-demo__option-summary'>{option.summary}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  renderResult() {
    const recommendation = this.recommendation();
    const hasDetails = recommendation.nextStep || Object.keys(this.state.answers).length > 0;

    return (
      <div className={this.recommendationTone(recommendation.key)}>
        <div className='license-navigator-v2-demo__result-label'>{this.i18nValue('recommendation')}</div>
        <h2 className='license-navigator-v2-demo__result-title'>{recommendation.title}</h2>
        <div className='license-navigator-v2-demo__result-section'>
          <div className='license-navigator-v2-demo__result-section-title'>{this.i18nValue('why')}</div>
          <p className='license-navigator-v2-demo__result-copy'>{recommendation.reason}</p>
        </div>
        <div className='license-navigator-v2-demo__result-actions'>
          {typeof this.props.onRecommendationConfigure === 'function' ? (
            <button
              type='button'
              className='license-navigator-v2-demo__cta'
              onClick={() => this.props.onRecommendationConfigure(recommendation)}
            >
              {this.configureActionLabel(recommendation)}
            </button>
          ) : null}
          {typeof this.props.onRecommendationSelect === 'function' ? (
            <button
              type='button'
              className='license-navigator-v2-demo__ghost'
              onClick={() => this.props.onRecommendationSelect(recommendation)}
            >
              {this.recommendationActionLabel(recommendation)}
            </button>
          ) : null}
          <button type='button' className='license-navigator-v2-demo__ghost' onClick={() => this.restart()}>
            {this.i18nValue('restart')}
          </button>
        </div>
        {hasDetails ? (
          <details className='license-navigator-v2-demo__result-details'>
            <summary className='license-navigator-v2-demo__result-details-toggle'>
              {this.i18nValue('result_details_toggle')}
            </summary>
            <div className='license-navigator-v2-demo__result-details-body'>
              <div className='license-navigator-v2-demo__result-section'>
                <div className='license-navigator-v2-demo__result-section-title'>{this.i18nValue('next_step')}</div>
                <p className='license-navigator-v2-demo__result-copy'>{recommendation.nextStep}</p>
              </div>
              <div className='license-navigator-v2-demo__result-section'>
                <div className='license-navigator-v2-demo__result-section-title'>{this.i18nValue('summary')}</div>
                {this.renderSummary()}
              </div>
            </div>
          </details>
        ) : null}
      </div>
    );
  }

  render() {
    const wrapperClasses = [
      'license-navigator-v2-demo',
      this.props.embedded ? 'license-navigator-v2-demo--embedded' : null
    ].filter(Boolean).join(' ');

    return (
      <div className={wrapperClasses}>
        {this.showIntro() ? (
          <div className='license-navigator-v2-demo__intro'>
            <h2 className='license-navigator-v2-demo__heading'>{this.i18nValue('heading')}</h2>
            <p className='license-navigator-v2-demo__subheading'>{this.i18nValue('subheading')}</p>
            <p className='license-navigator-v2-demo__intro-copy'>{this.i18nValue('start_copy')}</p>
          </div>
        ) : null}

        {this.props.showProgress !== false ? (
          <div className='license-navigator-v2-demo__progress'>
            <div className='license-navigator-v2-demo__progress-copy'>
              <span>{this.isComplete() ? this.i18nValue('result_done') : this.i18nValue('continue')}</span>
              <span>{this.progressPercent()}%</span>
            </div>
            <div className='license-navigator-v2-demo__progress-track'>
              <div className='license-navigator-v2-demo__progress-fill' style={{ width: `${this.progressPercent()}%` }}></div>
            </div>
          </div>
        ) : null}

        <div className='license-navigator-v2-demo__layout'>
          <div className='license-navigator-v2-demo__main'>
            {this.isComplete() ? this.renderResult() : this.renderCurrentQuestion()}
            <div className='license-navigator-v2-demo__actions'>
              <button
                type='button'
                className='license-navigator-v2-demo__ghost'
                onClick={() => this.goBack()}
                disabled={this.state.currentStepIndex === 0}
              >
                {this.i18nValue('back')}
              </button>
              <button
                type='button'
                className='license-navigator-v2-demo__ghost'
                onClick={() => this.restart()}
              >
                {this.i18nValue('restart')}
              </button>
            </div>
          </div>

          {this.showSidebar() ? (
            <aside className='license-navigator-v2-demo__sidebar'>
              <div className='license-navigator-v2-demo__sidebar-card'>
                <div className='license-navigator-v2-demo__sidebar-title'>
                  {this.props.sidebarTitle || this.i18nValue('summary')}
                </div>
                {this.renderSidebarContext()}
                {Object.keys(this.state.answers).length > 0 ? (
                  <div className='license-navigator-v2-demo__sidebar-section'>
                    {this.props.sidebarContextItems && this.props.sidebarContextItems.length > 0 ? (
                      <div className='license-navigator-v2-demo__sidebar-section-title'>{this.i18nValue('summary')}</div>
                    ) : null}
                    {this.renderSummary()}
                  </div>
                ) : (
                  <p className='license-navigator-v2-demo__sidebar-copy'>{this.i18nValue('start_copy')}</p>
                )}
                {typeof this.props.onSidebarAction === 'function' ? (
                  <button
                    type='button'
                    className='license-navigator-v2-demo__sidebar-action'
                    onClick={this.props.onSidebarAction}
                  >
                    {this.props.sidebarActionLabel}
                  </button>
                ) : null}
              </div>
            </aside>
          ) : null}
        </div>
      </div>
    );
  }
}

LicenseNavigatorV2.defaultProps = {
  locale: 'de',
  questions: [],
  inlineLicenseConfigPath: '/__test/inline_license_config',
  showIntro: true,
  showSidebar: true,
  sidebarTitle: null,
  sidebarContextItems: [],
  sidebarActionLabel: '',
  onSidebarAction: null,
  embedded: false,
  onRecommendationSelect: null,
  onRecommendationConfigure: null,
  recommendationActionLabel: null,
  configureActionLabel: null,
  autoAdvanceOnComplete: false,
  onStateChange: null,
  showProgress: true,
  i18n: {
    heading: 'Finden Sie die passende Lizenz für Ihr Projekt',
    subheading: '',
    back: 'Zurück',
    restart: 'Neu starten',
    continue: 'Weiter',
    recommendation: 'Wir empfehlen Ihnen diese Lizenzstufe:',
    why: 'Warum diese Empfehlung?',
    next_step: 'Nächster sinnvoller Schritt',
    summary: 'Ihre Antworten',
    result_details_toggle: 'Details zur Empfehlung und Ihren Antworten',
    start_copy: '',
    result_done: 'Empfehlung erstellt',
    solo_reason: 'Weil Sie als einzelne Person arbeiten und Ihr Projekt nach einem normalen Solo-Content-Setup aussieht. Dazu zählen hier bewusst auch Podcast- oder Webinar-Fälle, solange sie nicht in einen stärkeren Premium- oder Campaign-Raum kippen.',
    solo_next_step: 'Von hier aus kann direkt in die Solo-Quick-Config verzweigt werden.',
    business_reason: 'Weil Ihr Projekt im Unternehmens- oder Teamkontext liegt, aber noch kein Premium- oder Campaign-Fall ist. Auch Podcast- und Webinar-Projekte bleiben hier bewusst in Business, solange kein stärkerer Rechtekontext entsteht.',
    business_next_step: 'Hier würde als Nächstes ein schlanker Business-Konfigurator folgen.',
    premium_reason: 'Weil Sie einen stärkeren Rechtekontext mit Broadcast-, VOD- oder Trailer-Bezug beschrieben haben, aber noch im Rahmen eines einzelnen Hauptprojekts bleiben.',
    premium_next_step: 'Hier würde als Nächstes ein Premium-Flow für Territory, Nutzungsdauer und Module folgen.',
    campaign_reason: 'Weil Ihr Broadcast-/Trailer-/VOD-Fall nicht mehr nur ein einzelnes Hauptprojekt ist, sondern mehrere Assets oder Adaptionen im gemeinsamen Rahmen umfasst.',
    campaign_next_step: 'Hier würde als Nächstes ein Campaign-Flow für Kampagnenrahmen, Territory und Laufzeit folgen.'
  }
};

export default LicenseNavigatorV2;
